import { useMutation } from "convex/react";
import { X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAllDecks } from "@/hooks/useAllDecks";
import { useReviewTime } from "@/hooks/useReviewTime";
import { api } from "@convex/_generated/api";

const NEW_DECK_ID_PLACEHOLDER = "NEW_DECK_ID_PLACEHOLDER";

const SINGLE_SIDED_LOCAL_STORAGE_KEY = "defaultSingleSided";

interface AddCardDialogContentProps {
  closeDialog: () => void;
}

export const AddCardDialogContent = (props: AddCardDialogContentProps) => {
  const { deckId: maybeDeckId } = useParams();
  // AddCardDialogContentInternal has some local state that we want to be reset when deckId changes.
  return <AddCardDialogContentInternal key={maybeDeckId} {...props} maybeDeckId={maybeDeckId} />;
};

interface AddCardDialogContentInternalProps extends AddCardDialogContentProps {
  maybeDeckId?: string;
}

const AddCardDialogContentInternal = ({
  maybeDeckId,
  closeDialog,
}: AddCardDialogContentInternalProps) => {
  const [selectedMaybeDeckId, setSelectedMaybeDeckId] = useState<string>(
    maybeDeckId ?? NEW_DECK_ID_PLACEHOLDER
  );
  const decks = useAllDecks();

  // Make sure that we choose the first existing deck if we eventually find out that there are some decks.
  useEffect(() => {
    if (decks && decks.length > 0 && selectedMaybeDeckId === NEW_DECK_ID_PLACEHOLDER) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- we just verified that decks is not empty.
      setSelectedMaybeDeckId(decks[0]!._id);
    }
  }, [decks, selectedMaybeDeckId]);

  const questionRef = useRef<HTMLTextAreaElement>(null);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [singleSided, setSingleSided] = useState(
    localStorage.getItem(SINGLE_SIDED_LOCAL_STORAGE_KEY) === "true"
  );

  const toggleSingleSided = () => {
    setSingleSided((prev) => {
      const newValue = !prev;
      localStorage.setItem(SINGLE_SIDED_LOCAL_STORAGE_KEY, String(newValue));
      return newValue;
    });
  };

  const incompleteCard = singleSided ? !question : !question || !answer;
  const deckId = decks?.find((deck) => deck._id === selectedMaybeDeckId)?._id;
  const saveDisabled =
    incompleteCard || (!deckId && selectedMaybeDeckId !== NEW_DECK_ID_PLACEHOLDER);
  const reviewTime = useReviewTime();
  const addCardMutation = useMutation(api.cards.add);
  const addDeckMutation = useMutation(api.decks.add);
  const navigate = useNavigate();

  // TODO: this will get re-generated all the time (see dependencies array), which will cause the keyboard shortcuts to be constantly removed and re-added.
  const addCard = useCallback(
    async (addAnother = false) => {
      if (!saveDisabled) {
        if (deckId) {
          // TODO: add an optimistic update here.
          await addCardMutation({
            deckId,
            question,
            answer,
            singleSided,
            nextReview: reviewTime, // This ensures that the new card shows up immediately in the review queue.
          });
        } else if (selectedMaybeDeckId === NEW_DECK_ID_PLACEHOLDER) {
          const newDeckId = await addDeckMutation({
            name: "Random",
            cards: [
              {
                question,
                answer,
                nextReview: reviewTime, // This ensures that the new card shows up immediately in the review queue.
              },
            ],
          });
          await navigate(`/decks/${newDeckId}`);
        }
        setAnswer("");
        setQuestion("");
        if (!addAnother) {
          closeDialog();
        } else {
          questionRef.current?.focus();
        }
      }
    },
    [
      deckId,
      question,
      answer,
      singleSided,
      reviewTime,
      navigate,
      addCardMutation,
      addDeckMutation,
      saveDisabled,
      closeDialog,
      selectedMaybeDeckId,
    ]
  );

  const dropdownItems =
    selectedMaybeDeckId === NEW_DECK_ID_PLACEHOLDER
      ? [...(decks ?? []), { _id: NEW_DECK_ID_PLACEHOLDER, name: "(Create New Deck)" }]
      : decks;

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Cmd+Enter (or Ctrl+Enter on Windows)
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && !e.shiftKey && !saveDisabled) {
        e.preventDefault();
        void addCard(false);
      }
      // Check for Cmd+Shift+Enter (or Ctrl+Shift+Enter on Windows)
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && e.shiftKey && !saveDisabled) {
        e.preventDefault();
        void addCard(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [saveDisabled, addCard]);

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="text-xl">Add Card To Deck</DialogTitle>
        <DialogDescription hidden={true}>
          Create a new flashcard with a question and answer.
        </DialogDescription>
        <DialogClose className="absolute right-4 top-4 opacity-70 hover:opacity-100">
          <X className="h-4 w-4" />
        </DialogClose>
      </DialogHeader>
      <div className="space-y-4">
        {dropdownItems && (
          <Select
            value={selectedMaybeDeckId}
            onValueChange={(value) => setSelectedMaybeDeckId(value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a deck" />
            </SelectTrigger>
            <SelectContent>
              {dropdownItems.map((deck) => (
                <SelectItem key={deck._id} value={deck._id}>
                  {deck.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="border rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
          <Textarea
            placeholder="Front of card"
            className={`border-0 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none h-30`}
            value={question}
            ref={questionRef}
            onChange={(e) => setQuestion(e.target.value)}
          />
        </div>
        {!singleSided && (
          <div className="border rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
            <Textarea
              placeholder="Back of card"
              className={`border-0 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none h-30`}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
            />
          </div>
        )}
        <div className="flex items-center justify-end space-x-2">
          <Checkbox id="single-sided" onClick={toggleSingleSided} checked={singleSided} />
          <label htmlFor="single-sided">Single-sided</label>
        </div>
        <div className="flex justify-end space-x-2">
          <Button onClick={() => addCard(true)} disabled={saveDisabled}>
            Save and add another
          </Button>
          <Button onClick={() => addCard(false)} disabled={saveDisabled}>
            SAVE
          </Button>
        </div>
      </div>
    </DialogContent>
  );
};
